/**
 * Service per gestire le impostazioni dell'applicazione
 */

// Palette di colori disponibili
const colorPalettes = {
  default: {
    name: 'Default',
    colors: {
      primary: '#4285F4',      // Blu principale
      secondary: '#424242',    // Grigio scuro
      accent: '#9BBDF9',       // Blu chiaro per accenti
      error: '#FF5252',        // Rosso per errori
      info: '#64B5F6',         // Blu per informazioni
      success: '#66BB6A',      // Verde per successo
      warning: '#FFC107',      // Giallo per avvisi
      if65Blue: '#5C8ED6',     // Blu aziendale
      if65Silver: '#B0BEC5',   // Grigio argento
      if65Red: '#EF5350',      // Rosso aziendale
      if65Green: '#66BB6A',    // Verde aziendale
      if65Yellow: '#FFCA28',   // Giallo aziendale
      background: '#F5F5F5',   // Sfondo chiaro
    }
  },
  blueOcean: {
    name: 'Blu Oceano',
    colors: {
      primary: '#2C3E50',      // Blu scuro
      secondary: '#34495E',    // Blu-grigio
      accent: '#3498DB',       // Blu chiaro
      error: '#E74C3C',        // Rosso
      info: '#1ABC9C',         // Turchese
      success: '#2ECC71',      // Verde
      warning: '#F39C12',      // Arancione
      if65Blue: '#3498DB',     // Blu aziendale
      if65Silver: '#BDC3C7',   // Grigio argento
      if65Red: '#E74C3C',      // Rosso aziendale
      if65Green: '#2ECC71',    // Verde aziendale
      if65Yellow: '#F1C40F',   // Giallo aziendale
      background: '#ECF0F1',   // Sfondo chiaro
    }
  },
  greenNature: {
    name: 'Verde Natura',
    colors: {
      primary: '#27AE60',      // Verde
      secondary: '#2C3E50',    // Blu scuro
      accent: '#16A085',       // Verde acqua
      error: '#C0392B',        // Rosso scuro
      info: '#3498DB',         // Blu
      success: '#2ECC71',      // Verde chiaro
      warning: '#F39C12',      // Arancione
      if65Blue: '#3498DB',     // Blu aziendale
      if65Silver: '#BDC3C7',   // Grigio argento
      if65Red: '#C0392B',      // Rosso aziendale
      if65Green: '#27AE60',    // Verde aziendale
      if65Yellow: '#F1C40F',   // Giallo aziendale
      background: '#F5F5F5',   // Sfondo chiaro
    }
  },
  purpleElegance: {
    name: 'Viola Eleganza',
    colors: {
      primary: '#8E44AD',      // Viola
      secondary: '#2C3E50',    // Blu scuro
      accent: '#9B59B6',       // Viola chiaro
      error: '#E74C3C',        // Rosso
      info: '#3498DB',         // Blu
      success: '#2ECC71',      // Verde
      warning: '#F1C40F',      // Giallo
      if65Blue: '#3498DB',     // Blu aziendale
      if65Silver: '#BDC3C7',   // Grigio argento
      if65Red: '#E74C3C',      // Rosso aziendale
      if65Green: '#2ECC71',    // Verde aziendale
      if65Yellow: '#F1C40F',   // Giallo aziendale
      background: '#ECF0F1',   // Sfondo chiaro
    }
  },
  redEnergy: {
    name: 'Rosso Energia',
    colors: {
      primary: '#C0392B',      // Rosso scuro
      secondary: '#34495E',    // Blu-grigio
      accent: '#E74C3C',       // Rosso
      error: '#D35400',        // Arancione scuro
      info: '#3498DB',         // Blu
      success: '#27AE60',      // Verde
      warning: '#F39C12',      // Arancione
      if65Blue: '#3498DB',     // Blu aziendale
      if65Silver: '#BDC3C7',   // Grigio argento
      if65Red: '#C0392B',      // Rosso aziendale
      if65Green: '#27AE60',    // Verde aziendale
      if65Yellow: '#F39C12',   // Giallo aziendale
      background: '#ECF0F1',   // Sfondo chiaro
    }
  },
  grayNeutral: {
    name: 'Grigio Neutro',
    colors: {
      primary: '#607D8B',      // Blu-grigio
      secondary: '#455A64',    // Grigio scuro
      accent: '#78909C',       // Grigio-blu chiaro
      error: '#F44336',        // Rosso
      info: '#2196F3',         // Blu
      success: '#4CAF50',      // Verde
      warning: '#FF9800',      // Arancione
      if65Blue: '#2196F3',     // Blu aziendale
      if65Silver: '#B0BEC5',   // Grigio argento
      if65Red: '#F44336',      // Rosso aziendale
      if65Green: '#4CAF50',    // Verde aziendale
      if65Yellow: '#FFC107',   // Giallo aziendale
      background: '#F5F5F5',   // Sfondo chiaro
    }
  },
  earthTerra: {
    name: 'Terra',
    colors: {
      primary: '#CB6949',      // Marrone terra
      secondary: '#5D4037',    // Marrone scuro
      accent: '#BCAAA4',       // Beige chiaro
      error: '#D84315',        // Rosso terra
      info: '#795548',         // Marrone medio
      success: '#689F38',      // Verde oliva
      warning: '#F57C00',      // Arancione terra
      if65Blue: '#607D8B',     // Grigio-blu terra
      if65Silver: '#A1887F',   // Grigio terra
      if65Red: '#D84315',      // Rosso terra
      if65Green: '#689F38',    // Verde terra
      if65Yellow: '#F57C00',   // Giallo terra
      background: '#F5F4ED',   // Sfondo caldo terra
    }
  }
};

// Font disponibili
const availableFonts = [
  { value: 'Montserrat, sans-serif', name: 'Montserrat' },
  { value: 'Poppins, sans-serif', name: 'Poppins' },
  { value: 'Inter, sans-serif', name: 'Inter' }
];

// Chiavi per il localStorage
const SETTINGS_KEY = 'elefante_settings';
const COLOR_PALETTE_KEY = 'elefante_color_palette';
const FONT_FAMILY_KEY = 'elefante_font_family';

/**
 * Salva le impostazioni nel localStorage e sincronizza con il backend
 * @param {Object} settings - Le impostazioni da salvare
 */
const saveSettings = (settings) => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));

  // Se lo store è disponibile, sincronizziamo con il backend
  if (window.$store) {
    // Prepariamo le impostazioni per il backend
    const uiSettings = {
      theme: {
        colorPalette: settings.colorPalette,
        fontFamily: settings.fontFamily
      }
    };

    // Aggiorniamo le impostazioni nel backend (con gestione errori)
    window.$store.dispatch('updateUISettings', uiSettings)
      .catch(error => {
        console.warn('Impossibile sincronizzare le impostazioni con il backend:', error.response?.status);
        // Non blocchiamo l'applicazione se la sincronizzazione fallisce
      });
  }
};

/**
 * Carica le impostazioni dal localStorage o dallo store Vuex
 * @returns {Object} Le impostazioni caricate
 */
const loadSettings = () => {
  // Impostazioni predefinite
  const defaultSettings = {
    colorPalette: 'blueOcean',
    fontFamily: 'Montserrat, sans-serif'
  };

  // Controlliamo se ci sono impostazioni nello store Vuex
  if (window.$store && window.$store.state.uiSettings && window.$store.state.uiSettings.theme) {
    const storeSettings = window.$store.state.uiSettings.theme;
    return {
      colorPalette: storeSettings.colorPalette || defaultSettings.colorPalette,
      fontFamily: storeSettings.fontFamily || defaultSettings.fontFamily
    };
  }

  // Altrimenti carichiamo dal localStorage
  const settings = localStorage.getItem(SETTINGS_KEY);
  return settings ? JSON.parse(settings) : defaultSettings;
};

/**
 * Imposta la palette di colori
 * @param {string} paletteName - Il nome della palette di colori
 */
const setColorPalette = (paletteName) => {
  if (!colorPalettes[paletteName]) {
    console.error(`Palette di colori "${paletteName}" non trovata`);
    return;
  }

  const settings = loadSettings();
  settings.colorPalette = paletteName;
  saveSettings(settings);
  localStorage.setItem(COLOR_PALETTE_KEY, paletteName);

  // Applica i colori al tema
  applyColorPalette(paletteName);
};

/**
 * Applica la palette di colori al tema
 * @param {string} paletteName - Il nome della palette di colori
 */
const applyColorPalette = (paletteName) => {
  if (!colorPalettes[paletteName]) {
    console.error(`Palette di colori "${paletteName}" non trovata`);
    return;
  }

  const palette = colorPalettes[paletteName].colors;
  const root = document.documentElement;

  // Imposta le variabili CSS
  root.style.setProperty('--primary', palette.primary);
  root.style.setProperty('--secondary', palette.secondary);
  root.style.setProperty('--accent', palette.accent);
  root.style.setProperty('--error', palette.error);
  root.style.setProperty('--info', palette.info);
  root.style.setProperty('--success', palette.success);
  root.style.setProperty('--warning', palette.warning);
  root.style.setProperty('--if65-blue', palette.if65Blue);
  root.style.setProperty('--if65-silver', palette.if65Silver);
  root.style.setProperty('--if65-red', palette.if65Red);
  root.style.setProperty('--if65-green', palette.if65Green);
  root.style.setProperty('--if65-yellow', palette.if65Yellow);
  root.style.setProperty('--background', palette.background);

  // Salva i colori per applicarli quando Vuetify sarà disponibile
  const colors = {
    primary: palette.primary,
    secondary: palette.secondary,
    accent: palette.accent,
    error: palette.error,
    info: palette.info,
    success: palette.success,
    warning: palette.warning,
    if65Blue: palette.if65Blue,
    if65Silver: palette.if65Silver,
    if65Red: palette.if65Red,
    if65Green: palette.if65Green,
    if65Yellow: palette.if65Yellow
  };

  // Salva i colori per riferimento futuro
  window.vuetifyColors = colors;

  // Aggiorna il tema di Vuetify se la funzione è disponibile
  if (window.$updateVuetifyTheme) {
    window.$updateVuetifyTheme(colors);
  }
};

/**
 * Imposta il font dell'applicazione
 * @param {string} fontFamily - Il font da utilizzare
 */
const setFontFamily = (fontFamily) => {
  const settings = loadSettings();
  settings.fontFamily = fontFamily;
  saveSettings(settings);
  localStorage.setItem(FONT_FAMILY_KEY, fontFamily);

  // Applica il font al tema
  applyFontFamily(fontFamily);
};

/**
 * Applica il font al tema
 * @param {string} fontFamily - Il font da utilizzare
 */
const applyFontFamily = (fontFamily) => {
  try {
    // Salva il font per applicarlo quando Vuetify sarà disponibile
    window.vuetifyFont = fontFamily;

    // Imposta la variabile CSS
    const root = document.documentElement;
    root.style.setProperty('--font-family', fontFamily);

    // Crea o aggiorna un foglio di stile per forzare il font
    const styleId = 'settings-font-style';
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = `
      body, .v-application, .v-application .headline, .v-application .title,
      .v-application .subtitle-1, .v-application .subtitle-2,
      .v-application .body-1, .v-application .body-2,
      .v-application .caption, .v-application .overline {
        font-family: ${fontFamily} !important;
      }
    `;

    // Aggiorna il font in Vuetify se disponibile
    if (typeof window.$updateVuetifyFont === 'function') {
      window.$updateVuetifyFont(fontFamily);
    }
  } catch (error) {
    console.error('Errore nell\'applicazione del font:', error);
  }
};

/**
 * Inizializza le impostazioni all'avvio dell'applicazione
 */
const initSettings = () => {
  // Rendi lo store disponibile globalmente per il servizio
  if (window.$store === undefined && window.app && window.app.$store) {
    window.$store = window.app.$store;
  }

  const settings = loadSettings();

  // Applica la palette di colori
  applyColorPalette(settings.colorPalette || 'blueOcean');

  // Applica il font
  applyFontFamily(settings.fontFamily || 'Montserrat, sans-serif');

  // Salva le impostazioni per sincronizzarle con il backend
  // (solo se sono diverse da quelle predefinite)
  const defaultSettings = {
    colorPalette: 'blueOcean',
    fontFamily: 'Montserrat, sans-serif'
  };

  if (settings.colorPalette !== defaultSettings.colorPalette ||
      settings.fontFamily !== defaultSettings.fontFamily) {
    saveSettings(settings);
  }
};

export default {
  colorPalettes,
  availableFonts,
  loadSettings,
  saveSettings,
  setColorPalette,
  setFontFamily,
  initSettings
};
